import React, { useState, useEffect, memo } from 'react';
import { MessageSquare, Info, Plus, Upload, Send } from 'lucide-react';
import { addCaseNote, getCaseNotes, uploadCaseFile, getCaseFiles, deleteCaseFile, downloadFile } from '../../services/addService.service';
import { runInLane } from '../../utils/requestLane';
import { formatDateTime } from '../../utils/dateUtils';
import { getCurrentPatronId } from '../../utils/brandingUtils';
import { Section } from './workspacePrimitives';
import { useDialog } from '../../contexts/DialogContext';

const DownloadIcon = () => (
  <svg className="w-4 h-4 text-blue-600 hover:text-blue-800 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const DeleteIcon = () => (
  <svg className="w-4 h-4 text-red-600 hover:text-red-800 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const REASON_OPTIONS = [
  '- Select -',
  'Call received from sub,infmd about BD',
  'ASP searched, 3 way conference done and ASP activated',
  'Contact details of ASP attending BD',
  'Called subscriber for car details',
  'Followup with Technician/Driver',
  'SMS sent to subs',
  'SMS sent to ASP informing about KMS, Invoice Amt and Service Number',
  'Case closer call to subscriber to take feedback about service provided',
  'Total KMS Travelled: Toll Tax:Waiting Hours:',
  'ASP called for Case ID and we provided.',
  'Called ASP and asked for travelled route',
  'Called Customer care / Dealership and verified the car details',
  'Call received from Dealership/Customer care and gave car details',
  'Towing van is busy',
  'Technicain/Driver not available',
  'Garage/Workshop business closed'
];

const CaseNotesSegment = ({ caseId, onActivityCountsChange, refreshKey = 0 }) => {
  const { confirm } = useDialog();
  // Check if current patron is SDA to conditionally show/hide SMS segment
  const isCurrentPatronSDA = getCurrentPatronId() === 'sda';
  
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReason, setSelectedReason] = useState(REASON_OPTIONS[0]);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // File upload states
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDescription, setFileDescription] = useState('');
  const [fileError, setFileError] = useState('');
  const [fileSuccess, setFileSuccess] = useState('');

  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await runInLane(() => getCaseNotes(caseId));
      
      // The API response has the data directly, not under response.data
      if (response && response.items) {
        setNotes(response.items);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      setError('Failed to load notes');
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    if (!caseId) return;
    
    try {
      setLoadingFiles(true);
      setFileError('');
      const response = await runInLane(() => getCaseFiles(caseId));
      
      if (response && response.files) {
        setFiles(response.files);
      } else {
        setFiles([]);
      }
    } catch (err) {
      console.error('Failed to fetch files:', err);
      setFileError('Failed to load files');
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };



  useEffect(() => {
    if (caseId) {
      let cancelled = false;
      (async () => {
        await fetchNotes();
        if (!cancelled) await fetchFiles();
      })();
      return () => {
        cancelled = true;
      };
    }
    return undefined;
    // Intentionally re-fetch only when case or refresh key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, refreshKey]);

  useEffect(() => {
    onActivityCountsChange?.({ notes: notes.length, files: files.length });
  }, [notes.length, files.length, onActivityCountsChange]);

  const handleReasonChange = (e) => {
    const value = e.target.value;
    setSelectedReason(value);
    if (value !== REASON_OPTIONS[0]) {
      setNoteText(value);
    } else {
      setNoteText('');
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccess(false);
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await addCaseNote(caseId, {
        note_type: 'general',
        note_text: noteText,
        is_internal: false
      });
      setSuccess(true);
      setNoteText('');
      setSelectedReason(REASON_OPTIONS[0]);
      // Refresh notes after saving
      await fetchNotes();
    } catch (err) {
      setError(err.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setFileError('');
    setFileSuccess('');
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setFileError('Please select a file');
      return;
    }

    setUploading(true);
    setFileError('');
    setFileSuccess('');

    try {
      // Use file extension as file type
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      const fileType = fileExtension || 'unknown';
      
      await uploadCaseFile(caseId, selectedFile, fileType, fileDescription);
      setFileSuccess('File uploaded successfully!');
      setSelectedFile(null);
      setFileDescription('');
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
      // Refresh files list
      await fetchFiles();
    } catch (err) {
      setFileError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      if (file.download_url) {
        await downloadFile(file.download_url, file.file_name);
      } else {
        setFileError('Download URL not available');
      }
    } catch (err) {
      setFileError('Failed to download file');
    }
  };

  const handleFileDelete = async (fileId) => {
    const ok = await confirm({
      title: 'Delete file',
      message: 'Are you sure you want to delete this file?',
      confirmLabel: 'Delete',
    });
    if (!ok) return;

    try {
      await deleteCaseFile(caseId, fileId);
      setFileSuccess('File deleted successfully!');
      // Refresh files list
      await fetchFiles();
    } catch (err) {
      setFileError(err.message || 'Failed to delete file');
    }
  };

  const formatDate = (dateString) => {
    return formatDateTime(dateString);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const gridColsClass = isCurrentPatronSDA ? 'md:grid-cols-2' : 'md:grid-cols-3';

  return (
    <Section
      icon={<MessageSquare className="h-3.5 w-3.5" strokeWidth={2} />}
      title="Activity & communication"
      subtitle="Notes, files and outbound SMS"
      dense
    >
      <div className="mb-3 max-h-56 overflow-y-auto rounded-lg cwp-scrollbar-thin">
        {loading ? (
          <div className="flex h-24 items-center justify-center text-sm text-slate-500">Loading notes…</div>
        ) : notes.length === 0 ? null : (
          <div className="space-y-2 pr-1">
            {notes.map((note) => (
              <div
                key={note.id}
                className="rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm"
              >
                <div className="mb-1 flex flex-wrap justify-between gap-2">
                  <span className="font-medium text-slate-600">
                    {note.created_by?.name || 'Unknown'} · {note.note_type}
                  </span>
                  <span className="text-xs text-slate-500">{formatDate(note.created_at)}</span>
                </div>
                <p className="whitespace-pre-wrap text-slate-800">{note.note_text}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="mb-3 flex flex-col items-center justify-center gap-1 rounded-lg border border-dashed py-5"
        style={{ background: 'var(--cwp-card-muted)', borderColor: 'var(--cwp-border-strong)' }}
      >
        <Info className="h-4 w-4 text-slate-400" aria-hidden />
        <div className="text-[12.5px] font-medium text-slate-900">
          {notes.length === 0 ? 'No notes yet' : 'Add another note below'}
        </div>
        <div className="text-center text-[11px] text-slate-500">
          Use the panels below to add a note, file, or SMS.
        </div>
      </div>

      <div className={`grid grid-cols-1 gap-3 ${gridColsClass}`}>
        <div
          className="flex flex-col gap-2 rounded-lg p-3"
          style={{ background: 'var(--cwp-card-muted)', border: '1px solid var(--cwp-border)' }}
        >
          <div className="cwp-label-eyebrow text-slate-500">Add note</div>
          <select
            className="cwp-field w-full py-2 text-[12.5px] text-slate-700"
            value={selectedReason}
            onChange={handleReasonChange}
          >
            {REASON_OPTIONS.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
          <textarea
            className="cwp-field min-h-[4.5rem] resize-none text-[12.5px] placeholder:text-slate-400"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Note text…"
            rows={3}
          />
          {error ? <div className="text-xs text-red-600">{error}</div> : null}
          {success ? <div className="text-xs text-emerald-600">Note saved!</div> : null}
          <button
            type="button"
            className="cwp-btn-primary ml-auto inline-flex h-8 items-center gap-1 px-3 text-[12.5px]"
            onClick={handleSave}
            disabled={saving || !noteText.trim()}
          >
            <Plus className="h-3 w-3" aria-hidden />
            Save
          </button>
        </div>

        <div
          className="flex flex-col gap-2 rounded-lg p-3"
          style={{ background: 'var(--cwp-card-muted)', border: '1px solid var(--cwp-border)' }}
        >
          <div className="cwp-label-eyebrow text-slate-500">Upload file</div>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white py-4 transition hover:bg-slate-50">
            <Upload className="h-4 w-4 text-blue-600" aria-hidden />
            <span className="text-[12.5px] font-medium text-slate-900">Drop or browse</span>
            <span className="text-[10px] text-slate-500">Any file type</span>
            <input id="file-input" type="file" className="sr-only" onChange={handleFileSelect} accept="*/*" />
          </label>
          <input
            className="cwp-field text-[12.5px] placeholder:text-slate-400"
            value={fileDescription}
            onChange={(e) => setFileDescription(e.target.value)}
            placeholder="Description (optional)"
          />
          {fileError ? <div className="text-xs text-red-600">{fileError}</div> : null}
          {fileSuccess ? <div className="text-xs text-emerald-600">{fileSuccess}</div> : null}
          <button
            type="button"
            className="cwp-btn-primary ml-auto inline-flex h-8 items-center gap-1 px-3 text-[12.5px]"
            onClick={handleFileUpload}
            disabled={uploading || !selectedFile}
          >
            <Upload className="h-3 w-3" aria-hidden />
            Upload
          </button>
        </div>

        {!isCurrentPatronSDA ? (
          <div
            className="flex flex-col gap-2 rounded-lg p-3"
            style={{ background: 'var(--cwp-card-muted)', border: '1px solid var(--cwp-border)' }}
          >
            <div className="cwp-label-eyebrow text-slate-500">Send SMS</div>
            <select className="cwp-field mb-1 text-[12.5px]" defaultValue="">
              <option value="">- Select Type -</option>
            </select>
            <textarea
              className="cwp-field min-h-[4.5rem] resize-none text-[12.5px] placeholder:text-slate-400"
              rows={3}
              placeholder="Message preview…"
              readOnly
            />
            <button type="button" className="cwp-btn-primary ml-auto inline-flex h-8 items-center gap-1 px-3 text-[12.5px]">
              <Send className="h-3 w-3" aria-hidden />
              Send
            </button>
          </div>
        ) : null}
      </div>

      <div className="cwp-surface-card mt-4 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Case files</h3>
        {loadingFiles ? (
          <div className="flex h-16 items-center justify-center text-sm text-slate-500">Loading files…</div>
        ) : files.length === 0 ? (
          <div className="flex h-16 items-center justify-center gap-2 text-sm text-slate-500">
            <Info className="h-4 w-4 shrink-0" aria-hidden />
            <span>No files uploaded</span>
          </div>
        ) : (
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium text-slate-900">{file.file_name}</div>
                  <div className="text-xs text-slate-500">
                    {file.file_type} · {formatFileSize(file.file_size || 0)} · {formatDate(file.uploaded_at)}
                  </div>
                  {file.description ? (
                    <div className="mt-1 text-xs text-slate-600">{file.description}</div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleFileDownload(file)}
                    title="Download file"
                    className="rounded p-1 hover:bg-white"
                  >
                    <DownloadIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFileDelete(file.id)}
                    title="Delete file"
                    className="rounded p-1 hover:bg-white"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
};

export default memo(CaseNotesSegment); 